import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Button, Divider, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DashboardIcon from '@mui/icons-material/Dashboard';

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const quoteId = searchParams.get('quote_id');
    const paymentId = searchParams.get('razorpay_payment_id');
    const paymentLinkId = searchParams.get('razorpay_payment_link_id');
    const status = searchParams.get('razorpay_payment_link_status');

    // Automatically redirect to dashboard after 10 seconds if user does nothing
    useEffect(() => {
        const timer = setTimeout(() => {
            navigate('/admin/dashboard');
        }, 15000);
        return () => clearTimeout(timer);
    }, [navigate]);

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
                </Box>
                
                <Typography variant="h4" gutterBottom fontWeight="bold" color="primary.main">
                    Payment Successful!
                </Typography>
                
                <Typography variant="body1" color="text.secondary" paragraph>
                    Thank you for your purchase. Your payment for quote <strong>{quoteId}</strong> has been processed successfully.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ textAlign: 'left', mb: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight="medium" display="flex" alignItems="center">
                        <ReceiptIcon sx={{ mr: 1 }} /> Payment Details
                    </Typography>
                    <List dense>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Quote ID" 
                                secondary={quoteId || 'N/A'} 
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Payment ID" 
                                secondary={paymentId || 'N/A'} 
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Payment Link ID" 
                                secondary={paymentLinkId || 'N/A'} 
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Status" 
                                secondary={status?.toUpperCase() || 'PAID'} 
                                secondaryTypographyProps={{ color: 'success.main', fontWeight: 'bold' }}
                            />
                        </ListItem>
                    </List>
                </Box>

                <Box sx={{ bgcolor: 'info.lighter', p: 2, borderRadius: 2, mb: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="info.main">
                        Your credits have been automatically allocated to your account.
                        You can now start using them for job postings and candidate processing.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        startIcon={<DashboardIcon />}
                        onClick={() => navigate('/admin/dashboard')}
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        Go to Dashboard
                    </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
                    Redirecting to dashboard in 15 seconds...
                </Typography>
            </Paper>
        </Container>
    );
};

export default PaymentSuccess;
